import React, { useEffect, useState } from 'react';
import styles from './style/Dashboard.module.scss';
import Modal from '../Utilities/Modal';
import Table from '../Utilities/Table';
import Button from '../Utilities/Button';
import Paginate from '../Utilities/Paginate';
import { MdClear, MdMarkEmailRead } from 'react-icons/md';
import EmptyBox from '../Utilities/EmptyBox';
import { useDispatch, useSelector } from 'react-redux';
import { getSubscriptionHistory, getEnterpriseOrders, resendInvoiceMail, selectEnterpriseCompany } from '../../store/slice/manager/enterpriseCompany';
import LoadingTable from '../Utilities/LoadingTable';
import moment from 'moment';
import { getServiceName } from '../../constant/SERVICE_NAME_MAP';

const ModalHistory = ({ isOpen, setIsOpen, enterpriseId, serviceId, setEnterpriseId, setServiceId }) => {
  const headerData =
    serviceId === 8
      ? [
          { title: '日期', width: 13 },
          { title: '說明', width: 27 },
          { title: '付款方式', width: 13 },
          { title: '付款金額', width: 17 },
          { title: '付款狀態', width: 17 },
          { title: '動作', width: 13 },
        ]
      : [
          { title: '扣款日期', width: 8 },
          { title: '更換日期', width: 8 },
          { title: '月租方案', width: 18 },
          { title: '贈送人數', width: 6 },
          { title: '綁約天數', width: 6 },
          { title: '開始日期', width: 9 },
          { title: '結束日期', width: 9 },
          { title: '付款方式', width: 10 },
          { title: '付款金額', width: 7 },
          { title: '付款狀態', width: 11 },
          { title: '動作', width: 8 },
        ];

  const dispatch = useDispatch();
  const { history, loading } = useSelector(selectEnterpriseCompany);
  const [page, setPage] = useState(1);
  const [resendingKeys, setResendingKeys] = useState([]);
  const [resendModal, setResendModal] = useState({ open: false, target: null, taxId: '' });
  const limit = 15;

  const openResendModal = (data) => {
    const orderUuid = data.uuid || data.id;
    const transactionLogId = data.content?.[0]?.transaction_log_id || null;
    const key = `${orderUuid}:${transactionLogId || 0}`;
    if (resendingKeys.includes(key)) return;
    // 有 tax_id 就帶進來顯示，沒有就留空
    const existingTaxId = data.tax_id || data.taxid || data.content?.[0]?.tax_id || '';
    setResendModal({ open: true, target: data, taxId: existingTaxId });
  };

  const submitResendInvoice = async () => {
    const data = resendModal.target;
    if (! data) return;
    const orderUuid = data.uuid || data.id;
    const transactionLogId = data.content?.[0]?.transaction_log_id || null;
    const key = `${orderUuid}:${transactionLogId || 0}`;
    const taxId = (resendModal.taxId || '').trim();
    if (taxId && ! /^\d{8}$/.test(taxId)) {
      window.alert('統一編號需為 8 位數字');
      return;
    }
    setResendModal({ open: false, target: null, taxId: '' });
    setResendingKeys((prev) => [...prev, key]);
    try {
      const res = await dispatch(resendInvoiceMail({ serviceId, orderUuid, transactionLogId, taxId: taxId || null })).unwrap();
      const msgMap = { issued: '已開立並寄出發票', reissued: '已作廢舊發票並重新開立', resent: '已重新寄出發票' };
      window.alert(msgMap[res?.action] || '已處理');
    } catch (e) {
      window.alert('補送失敗：' + (e?.message || '請查看後端 log'));
    } finally {
      setResendingKeys((prev) => prev.filter((k) => k !== key));
    }
  };

  useEffect(() => {
    if (enterpriseId) {
      const postObj = {
        enterprise_id: enterpriseId,
        service_id: serviceId,
        page: 1,
        limit: limit,
      };
      if (serviceId === 8) {
        dispatch(getEnterpriseOrders(postObj));
      } else {
        dispatch(getSubscriptionHistory(postObj));
      }
    }
  }, [enterpriseId, serviceId, dispatch]);

  const pageChangeHandler = (e) => {
    setPage(parseInt(e.selected + 1));
    const postObj = {
      enterprise_id: enterpriseId,
      service_id: serviceId,
      page: e.selected + 1,
      limit: limit,
    };
    if (serviceId === 8) {
      dispatch(getEnterpriseOrders(postObj));
    } else {
      dispatch(getSubscriptionHistory(postObj));
    }
  };

  return (
    <Modal isOpen={isOpen} size='xxl'>
      <div className={styles.ModalContainer}>
        <div className={`${styles.modalHeader} ${styles.special}`}>
          <div className={styles.modalTitle}>訂閱購買紀錄</div>
          <Button
            type='tertiary'
            icon={<MdClear />}
            onClick={() => {
              setIsOpen(false);
              setEnterpriseId('');
              setServiceId('');
              setPage(1);
            }}
          />
        </div>
        <div className={`${styles.modalBody} ${styles.nonOverflow}`}>
          <Table header={headerData}>
            {loading ? (
              <LoadingTable row={10} column={headerData.length} />
            ) : history?.rows?.length > 0 ? (
              <tbody>
                {history?.rows?.map((data, index) => (
                  <tr key={index}>
                    {serviceId === 8 ? (
                      <>
                        <td width={`${headerData[0].width}%`}>{moment(data.created_at || data.createdAt).format('YYYY-MM-DD')}</td>
                        <td width={`${headerData[1].width}%`}>
                          {data?.items?.length > 0 && data?.items[0]?.item_name}
                        </td>
                        <td width={`${headerData[2].width}%`}>
                          {(data.payment_status === 'paid') &&
                            (data.payment_method_type === null || data.advanced_type === 'gift-charge' || data.type === 'gift-cancel') &&
                            '中控贈送'}
                          {(data.payment_status === 'cancelled') &&
                            (data.advanced_type === 'gift-charge' || data.type === 'gift-cancel') &&
                            '取消贈送'}
                          {(data.payment_status === 'paid') && data.payment_method_type === 'credit' && '信用卡扣款'}
                          {(data.payment_status === 'paid') && data.payment_method_type === 'referral' && '推薦碼贈送'}
                          {(data.payment_status === 'paid') && data.payment_method_type === 'voucher' && '優惠碼贈送'}
                          {(data.payment_status === 'unauth') && '未授權'}
                        </td>
                        <td width={`${headerData[3].width}%`}>
                          <span className={styles.textPrimary}>
                            {data?.items?.length > 0 ? (
                              'NT$ ' + (Number(new URLSearchParams(data?.items[0]?.item_service_code_2)?.get("points"))?.toLocaleString())
                            ) : (
                              <b>Free</b>
                            )}
                          </span>
                        </td>
                        <td width={`${headerData[4].width}%`}>
                          {data.payment_status === 'paid' ? (
                            <span className={styles.success}>付款成功</span>
                          ) : data.payment_status === 'cancelled' || data.payment_status === 'failed' ? (
                            <span className={styles.failed}>已取消</span>
                          ) : (
                            <span className={styles.failed}>付款失敗</span>
                          )}
                        </td>
                        <td width={`${headerData[5].width}%`}>
                          {data.payment_status === 'paid' && (
                            <span title='補送發票'>
                              <Button
                                type='tertiary'
                                icon={<MdMarkEmailRead />}
                                disabled={resendingKeys.includes(`${data.uuid || data.id}:0`)}
                                onClick={() => openResendModal(data)}
                              />
                            </span>
                          )}
                        </td>
                      </>
                    ) : (
                      data.content?.length > 0 &&
                      <>
                        <td width={`${headerData[0].width}%`}>{moment(data.created_at).format('YYYY-MM-DD')}</td>
                        <td width={`${headerData[1].width}%`}>{data.content[0]?.change_date ? data.content[0]?.change_date : '-'}</td>
                        <td width={`${headerData[2].width}%`}>
                          {data.content.map((item) => (
                            <div key={item.service_id}>
                              {getServiceName(item.service_name)}/
                              {item.additional_quantity + (item.spec === 'change' ? 0 : item.plan.users_count_upper_limit)}人
                              {(item.payment_amount || item.payment_amount > 0) && <>/ NT${item.payment_amount?.toLocaleString()}</>}
                            </div>
                          ))}
                        </td>
                        <td width={`${headerData[3].width}%`}>{data.content[0].gift_quantity ? data.content[0].gift_quantity + ' 人' : '-'}</td>
                        <td width={`${headerData[4].width}%`}>{data.content[0].validity_days}天</td>
                        <td width={`${headerData[5].width}%`}>{data.content[0].start_date}</td>
                        <td width={`${headerData[6].width}%`}>{data.content[0].expiration_date}</td>
                        <td width={`${headerData[7].width}%`}>{data.content[0].payment_method}</td>
                        <td width={`${headerData[8].width}%`}>
                          <span className={styles.textPrimary}>
                            {data?.total_amount > 0 ? 'NT$' + data?.total_amount?.toLocaleString() : <b>Free</b>}
                          </span>
                        </td>
                        <td width={`${headerData[9].width}%`}>
                          {data.content[0].payment_succeeded ? (
                            <span className={styles.success}>付款成功</span>
                          ) : (
                            <span className={styles.failed}>付款失敗</span>
                          )}
                        </td>
                        <td width={`${headerData[10].width}%`}>
                          {data.content[0].payment_succeeded && (
                            <span title='補送發票'>
                              <Button
                                type='tertiary'
                                icon={<MdMarkEmailRead />}
                                disabled={resendingKeys.includes(`${data.uuid || data.id}:${data.content[0].transaction_log_id || 0}`)}
                                onClick={() => openResendModal(data)}
                              />
                            </span>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            ) : (
              <tbody style={{ height: '450px' }}>
                <tr>
                  <td colSpan={headerData.length}>
                    <EmptyBox type={1} caption={serviceId === 8 ? '目前還未有任何購買紀錄' : '目前還未有任何訂閱購買紀錄'} />
                  </td>
                </tr>
              </tbody>
            )}
          </Table>
        </div>
        <div className={`${styles.modalFooter} ${styles.normal}`}>
          {history?.rows?.length > 0 && (
            <Paginate
              onPageChange={pageChangeHandler}
              pageCount={page}
              limit={limit}
              totalPages={Math.ceil(history?.total / limit)}
              totalData={history?.total}
            />
          )}
        </div>
      </div>
      <Modal isOpen={resendModal.open} width='480px' height='auto'>
        <div style={{ padding: '24px 28px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontSize: 18, fontWeight: 600 }}>補送發票</div>
            <Button
              type='tertiary'
              icon={<MdClear />}
              onClick={() => setResendModal({ open: false, target: null, taxId: '' })}
            />
          </div>

          <div style={{ fontSize: 14, color: '#555', marginBottom: 16, lineHeight: 1.5 }}>
            如要開立公司發票請填統一編號（8 位數字）；不填則開立個人雲端發票。
          </div>
          <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>
            統一編號
          </label>
          <input
            type='text'
            value={resendModal.taxId}
            maxLength={8}
            placeholder='不填則開立個人雲端發票'
            onChange={(e) => setResendModal((s) => ({ ...s, taxId: e.target.value.replace(/\D/g, '') }))}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d9d9d9',
              borderRadius: 6,
              fontSize: 14,
              boxSizing: 'border-box',
              marginBottom: 20,
            }}
          />

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button
              type='secondary'
              onClick={() => setResendModal({ open: false, target: null, taxId: '' })}
              title='取消'
            />
            <Button
              type='primary'
              onClick={submitResendInvoice}
              title='確認補送'
            />
          </div>
        </div>
      </Modal>
    </Modal>
  );
};

export default ModalHistory;
